import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Anchor, Button, Code, Group, Text, ThemeIcon } from '@mantine/core';
import { IconArrowLeft, IconFish, IconLayoutDashboard } from '@tabler/icons-react';
import { ErrorPageLayout } from '@/components/layout/ErrorPageLayout';

function Digit({ children }: { children: string }): JSX.Element {
  return (
    <Text component="span" className="font-heading" fw={700} fz={88} lh={1} c="teal.6">
      {children}
    </Text>
  );
}

/**
 * 404 page for URLs that match no route.
 *
 * Rendered by the catch-all route and by RouteErrorPage when the router
 * reports a 404, so it must work without AuthContext.
 */
export function NotFoundPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  // React Router gives the first entry of a session the key "default", so
  // anything else means there is an in-app page to go back to.
  const canGoBack = location.key !== 'default';

  return (
    <ErrorPageLayout
      hero={
        <Group gap={6} justify="center" wrap="nowrap" aria-hidden="true">
          <Digit>4</Digit>
          <ThemeIcon
            size={64}
            radius={1000}
            variant="light"
            color="teal"
            style={{ border: '9px solid var(--mantine-color-teal-6)' }}
          >
            <IconFish size={28} stroke={2} className="rdh-bob" />
          </ThemeIcon>
          <Digit>4</Digit>
        </Group>
      }
      title="Page not found"
      description={
        <>
          We couldn&apos;t find anything at{' '}
          <Code bg="gray.2" style={{ wordBreak: 'break-all' }}>
            {location.pathname}
          </Code>
          . The link may be out of date, or the page may have moved.
        </>
      }
      actions={
        <>
          <Button component={Link} to="/" leftSection={<IconLayoutDashboard size={16} />}>
            Go to dashboard
          </Button>
          {canGoBack && (
            <Button
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate(-1)}
            >
              Go back
            </Button>
          )}
        </>
      }
      footer={
        <>
          Looking for the free reef tools?{' '}
          <Anchor component={Link} to="/tools" size="sm">
            Browse tools
          </Anchor>
        </>
      }
    />
  );
}
